/**
 * Reject Supabase server keys in browser bundles (causes "Forbidden use of
 * secret API key in browser", and exposes a key that bypasses RLS to anyone who
 * opens devtools).
 *
 * Two key formats have to be covered. The original check decoded a JWT and
 * looked for role service_role, but the newer `sb_secret_...` keys are not JWTs:
 * `key.split('.')[1]` is undefined, atob threw, the catch swallowed it, and the
 * server key sailed through into the bundle.
 */

/** @returns {string} An empty string when the key is browser-safe. */
export function describeServerKey(key) {
  if (!key || typeof key !== 'string') return '';
  const value = key.trim();

  if (value.startsWith('sb_secret_')) return 'a secret key (sb_secret_…)';
  if (value.startsWith('sbp_')) return 'a Supabase personal access token (sbp_…)';

  const segments = value.split('.');
  if (segments.length === 3) {
    try {
      const payload = JSON.parse(
        atob(segments[1].replace(/-/g, '+').replace(/_/g, '/'))
      );
      if (payload?.role === 'service_role') return 'a service_role JWT';
    } catch {
      /* not decodable — treated as opaque below */
    }
  }
  return '';
}

export function assertBrowserSupabaseAnonKey(key, label = 'REACT_APP_SUPABASE_ANON_KEY') {
  const problem = describeServerKey(key);
  if (problem) {
    throw new Error(
      `[Supabase] ${label} is ${problem}. Browsers must use the anon (publishable) key — ` +
        'a server key here bypasses row-level security for anyone who reads the bundle. ' +
        'Set the anon key in frontend/wrangler.toml or Cloudflare Pages, and rotate the exposed key.'
    );
  }
  return key;
}
